import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import { useAudienceRulesPreview } from '@/api/audience/audienceAPI';
import { Button } from '@/components/buttons';
import DonutGauge from '@/components/charts/DonutGauge';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import { LegendItem } from '@/components/Legend';
import { COLOR_AQUA_200, COLOR_AQUA_600 } from '@/styles/palette';

import RuleBuilderPreviewModalDetailRow from './RuleBuilderPreviewModalDetailRow';

type Props = {
  rulesConfig?: string;
  onClose: () => void;
};

const RuleBuilderPreviewDialog = ({ rulesConfig, onClose }: Props) => {
  const { previewData, error, isLoading } = useAudienceRulesPreview(rulesConfig);
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) onClose();
  };

  return (
    <Dialog open={!!rulesConfig} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Audience Preview</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Compare your audience against total engaged companies and visits over the last 90
              days.
            </Trans>
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <div className="flex h-[327px] flex-col gap-2">
              <div className="flex justify-center gap-2">
                <LegendItem
                  className="max-w-[inherit]"
                  value={t`Engaged Companies`}
                  name={t`Engaged Companies`}
                  color={COLOR_AQUA_600}
                  noToggleVisibility
                />
                <LegendItem
                  className="max-w-[inherit]"
                  value={t`Visits`}
                  name={t`Visits`}
                  color={COLOR_AQUA_200}
                  noToggleVisibility
                />
              </div>
              <DonutGauge
                outerSeriesName={t`Engaged Companies`}
                innerSeriesName={t`Visits`}
                outerValue={
                  previewData && previewData.totalEngagedCompanies
                    ? (previewData.audienceEngagedCompanies / previewData.totalEngagedCompanies) *
                      100
                    : 0
                }
                outerCount={previewData?.audienceEngagedCompanies}
                innerValue={
                  previewData && previewData.totalVisits
                    ? (previewData.audienceVisits / previewData.totalVisits) * 100
                    : 0
                }
                innerCount={previewData?.audienceVisits}
                isLoading={isLoading}
                error={error}
                hideLegend
              />
            </div>
          </div>
          <div>
            <div className="mt-[38px] flex flex-col">
              <RuleBuilderPreviewModalDetailRow
                label={<Trans>Engaged Companies</Trans>}
                value={previewData?.audienceEngagedCompanies}
                total={previewData?.totalEngagedCompanies}
                isLoading={isLoading}
                error={error}
              />
              <RuleBuilderPreviewModalDetailRow
                label={<Trans>Visits</Trans>}
                value={previewData?.audienceVisits}
                total={previewData?.totalVisits}
                isLoading={isLoading}
                error={error}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <DialogClose
            render={
              <Button variant="on-primary" size="large" isLoading={isLoading} autoFocus>
                <Trans>Close</Trans>
              </Button>
            }
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RuleBuilderPreviewDialog;
